"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConditionsBuilder = exports.MapWithFallbackForProductHack = void 0;
const lodash_1 = __importDefault(require("lodash"));
const date_1 = require("../condition-engine/helpers/date");
const queryOperators_1 = require("../condition-engine/queryOperators");
const AnswerPath_1 = require("../questionnaire-engine/answersResolver/AnswerPath");
const dateTime_1 = require("../types/carrierQuestionnaire/conditions/dateTime");
const operators_1 = require("../types/carrierQuestionnaire/conditions/operators");
const queryOperators_2 = require("../types/carrierQuestionnaire/conditions/queryOperators");
const conditionBlueprints_1 = require("../types/questionnaireBlueprints/conditionBlueprints");
const fetchJointProductSurrogateId_1 = require("./fetchJointProductSurrogateId");
class MapWithFallbackForProductHack {
    nodeIdParents;
    nodeIdToAnswerPathMap;
    constructor(blueprint, nodeIdToAnswerPathMap) {
        this.nodeIdToAnswerPathMap = nodeIdToAnswerPathMap;
        this.nodeIdParents = MapWithFallbackForProductHack.getMapFromBlueprint(blueprint);
    }
    get(nodeId) {
        const valueFromBlueprint = this.nodeIdParents.get(nodeId);
        if (valueFromBlueprint) {
            return valueFromBlueprint;
        }
        if (this.nodeIdToAnswerPathMap) {
            return MapWithFallbackForProductHack.getParentNodeIdsFromNodeIdAnswerPathMap(nodeId, this.nodeIdToAnswerPathMap);
        }
        return [];
    }
    has(nodeId) {
        const value = this.nodeIdParents.has(nodeId);
        if (value === true) {
            return true;
        }
        if (this.nodeIdToAnswerPathMap) {
            return this.nodeIdToAnswerPathMap.has(nodeId);
        }
        return false;
    }
    static getMapFromBlueprint(blueprint) {
        const nodeIdParents = new Map();
        const sectionGroupKeys = Object.keys(blueprint.sectionGroupBlueprints);
        for (const key of sectionGroupKeys) {
            const sectionGroup = blueprint.sectionGroupBlueprints[key];
            const sectionGroupNodeId = sectionGroup.repeatable?.repeatableAnswerNodeId;
            if (sectionGroupNodeId) {
                nodeIdParents.set(sectionGroupNodeId, []);
            }
            const sections = blueprint.sectionBlueprints.filter((e) => e.sectionGroupKey === key);
            for (const section of sections) {
                for (const subsection of section.subsections) {
                    for (const fieldGroup of subsection.fieldGroups) {
                        const questionNodeId = fieldGroup.repeatable?.repeatableAnswerNodeId;
                        if (questionNodeId) {
                            nodeIdParents.set(questionNodeId, [sectionGroupNodeId].filter((e) => e !== undefined));
                        }
                        for (const field of fieldGroup.fields) {
                            if (field.answerNodeId) {
                                nodeIdParents.set(field.answerNodeId, [questionNodeId, sectionGroupNodeId].filter((e) => e !== undefined));
                            }
                        }
                    }
                }
            }
        }
        return nodeIdParents;
    }
    static getParentNodeIdsFromNodeIdAnswerPathMap(targetNodeId, nodeIdToAnswerPathMap) {
        const ancestorCollectionNodeIds = [];
        const targetNodeIdAnswerPath = nodeIdToAnswerPathMap.get(targetNodeId);
        if (!targetNodeIdAnswerPath) {
            return ancestorCollectionNodeIds;
        }
        const ancestorCollectionAnswerPath = AnswerPath_1.AnswerPath.getAncestorCollectionAnswerPath(targetNodeIdAnswerPath);
        if (!ancestorCollectionAnswerPath?.nodeId) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            ancestorCollectionNodeIds;
        }
        else {
            ancestorCollectionNodeIds.push(ancestorCollectionAnswerPath?.nodeId);
            const furtherAncestorCollections = this.getParentNodeIdsFromNodeIdAnswerPathMap(ancestorCollectionAnswerPath?.nodeId, nodeIdToAnswerPathMap);
            ancestorCollectionNodeIds.push(...furtherAncestorCollections);
        }
        return ancestorCollectionNodeIds;
    }
}
exports.MapWithFallbackForProductHack = MapWithFallbackForProductHack;
class ConditionsBuilder {
    nodeIdParents;
    constructor(blueprint, nodeIdToAnswerPathMap) {
        this.nodeIdParents = new MapWithFallbackForProductHack(blueprint, nodeIdToAnswerPathMap);
    }
    buildConditions(conditionValues) {
        if (!conditionValues) {
            return undefined;
        }
        if ((0, conditionBlueprints_1.isBlueprintConditionsValue)(conditionValues)) {
            const conditionsArray = conditionValues.conditions
                .map((condition) => this.buildConditions(condition))
                .filter(Boolean);
            if (conditionValues?.booleanOperator) {
                return {
                    conditions: conditionsArray,
                    operator: this.toBooleanOperator(conditionValues.booleanOperator),
                };
            }
            return {
                conditions: conditionsArray,
            };
        }
        const condition = this.buildCondition(conditionValues);
        const targetNodeIds = this.getTargetNodeIdsFromCondition(conditionValues);
        const ancestorCollectionNodeIds = targetNodeIds && targetNodeIds.length ? this.nodeIdParents.get(targetNodeIds[0]) || [] : [];
        const isCollectionCondition = !!ancestorCollectionNodeIds.length && !!conditionValues?.collectionOperators;
        if (isCollectionCondition) {
            const areTargetNodeIdsInTheSameCollection = targetNodeIds.every((nodeId) => (this.nodeIdParents.get(nodeId) || [])[0] === ancestorCollectionNodeIds[0]);
            if (!areTargetNodeIdsInTheSameCollection) {
                throw new Error('Target nodeIds must be part of the same collection');
            }
            // Collection ancestors should be put in a collection condition if they are not using the 'local' context.
            let collectionCondition = condition;
            for (const collectionNodeId of ancestorCollectionNodeIds) {
                const blueprintCollectionOperator = conditionValues?.collectionOperators?.[collectionNodeId];
                const collectionOperator = this.toCollectionOperator(blueprintCollectionOperator);
                if (collectionOperator && blueprintCollectionOperator !== conditionBlueprints_1.BlueprintCollectionOperators.thisItem) {
                    collectionCondition = {
                        collection: collectionNodeId,
                        collectionOperator,
                        conditions: [collectionCondition],
                    };
                }
            }
            return {
                conditions: [collectionCondition],
                operator: operators_1.BooleanOperators.and,
            };
        }
        return {
            conditions: [condition],
            operator: operators_1.BooleanOperators.and,
        };
    }
    buildCondition(conditionValue) {
        switch (conditionValue.type) {
            case conditionBlueprints_1.ConditionBlueprintType.ageRange: {
                return this.generateAgeRangeCondition(conditionValue);
            }
            case conditionBlueprints_1.ConditionBlueprintType.bmiRange: {
                return this.generateBMIRangeCondition(conditionValue);
            }
            case conditionBlueprints_1.ConditionBlueprintType.characterCountInBetween: {
                return this.generateCharacterCountInBetweenCondition(conditionValue);
            }
            case conditionBlueprints_1.ConditionBlueprintType.countEqual: {
                return this.generateCountEqualCondition(conditionValue);
            }
            case conditionBlueprints_1.ConditionBlueprintType.dateComparison: {
                const { startDateNodeId, endDateNodeId, unit, value, operator } = conditionValue;
                return {
                    query: {
                        select: [endDateNodeId, startDateNodeId],
                        operator: queryOperators_2.QueryOperators.subtractDates,
                        operatorParams: {
                            dateUnit: unit,
                        },
                    },
                    operator: this.numberComparisonConditionOperatorToComparisonOperator(operator),
                    value,
                };
            }
            case conditionBlueprints_1.ConditionBlueprintType.emptiness: {
                const { targetNodeId, isEmpty } = conditionValue;
                const comparisonOperator = isEmpty ? operators_1.ComparisonOperators.isEmpty : operators_1.ComparisonOperators.notEmpty;
                return {
                    operator: comparisonOperator,
                    nodeId: targetNodeId,
                };
            }
            case conditionBlueprints_1.ConditionBlueprintType.engineCondition: {
                return this.generateEngineCondition(conditionValue);
            }
            case conditionBlueprints_1.ConditionBlueprintType.equality: {
                const { targetNodeId, value, isEqual, nodeIdOfValue } = conditionValue;
                const comparisonOperator = isEqual ? operators_1.ComparisonOperators.equal : operators_1.ComparisonOperators.notEqual;
                return {
                    operator: comparisonOperator,
                    nodeId: targetNodeId,
                    value: value,
                    controlValue: nodeIdOfValue,
                };
            }
            case conditionBlueprints_1.ConditionBlueprintType.futureDate: {
                return this.generateFutureDateCondition(conditionValue);
            }
            case conditionBlueprints_1.ConditionBlueprintType.instancesCount: {
                return this.generateInstancesCountCondition(conditionValue);
            }
            case conditionBlueprints_1.ConditionBlueprintType.jointProductAgeRange: {
                return this.generateJointProductAgeRangeCondition(conditionValue);
            }
            case conditionBlueprints_1.ConditionBlueprintType.lastIncidentDate: {
                return this.generateLastIncidentDateCondition(conditionValue);
            }
            case conditionBlueprints_1.ConditionBlueprintType.matches: {
                const { targetNodeId, value, quantifier } = conditionValue;
                let comparisonOperator;
                switch (quantifier) {
                    case conditionBlueprints_1.MatchesConditionPropertyQuantifiers.any:
                        comparisonOperator = operators_1.ComparisonOperators.matchesAny;
                        break;
                    case conditionBlueprints_1.MatchesConditionPropertyQuantifiers.none:
                        comparisonOperator = operators_1.ComparisonOperators.matchesNone;
                        break;
                }
                return {
                    operator: comparisonOperator,
                    nodeId: targetNodeId,
                    value,
                };
            }
            case conditionBlueprints_1.ConditionBlueprintType.matchesRegex: {
                return this.generateMatchesRegexCondition(conditionValue);
            }
            case conditionBlueprints_1.ConditionBlueprintType.mathOperator: {
                return this.generateMathOperatorCondition(conditionValue);
            }
            case conditionBlueprints_1.ConditionBlueprintType.numberComparison: {
                return this.generateNumberComparisonCondition(conditionValue);
            }
            case conditionBlueprints_1.ConditionBlueprintType.percentOf: {
                const { targetNodeId, value, nodeIdOfValue, operator, percent } = conditionValue;
                return {
                    query: {
                        select: [targetNodeId],
                        operator: queryOperators_2.QueryOperators.multiply,
                        operatorParams: {
                            numeric: [percent / 100],
                        },
                    },
                    operator: this.numberComparisonConditionOperatorToComparisonOperator(operator),
                    ...(value ? { value } : { controlValue: nodeIdOfValue }),
                };
            }
            case conditionBlueprints_1.ConditionBlueprintType.reflexive: {
                const { targetNodeId, value } = conditionValue;
                return {
                    operator: operators_1.ComparisonOperators.equal,
                    nodeId: targetNodeId,
                    value,
                };
            }
        }
    }
    getTargetNodeIdsFromCondition(conditionValues) {
        switch (conditionValues.type) {
            case conditionBlueprints_1.ConditionBlueprintType.ageRange:
                return [conditionValues.targetBirthdateNodeId];
            case conditionBlueprints_1.ConditionBlueprintType.bmiRange:
                return [conditionValues.heightNodeId, conditionValues.weightNodeId];
            case conditionBlueprints_1.ConditionBlueprintType.characterCountInBetween:
            case conditionBlueprints_1.ConditionBlueprintType.countEqual:
            case conditionBlueprints_1.ConditionBlueprintType.equality:
            case conditionBlueprints_1.ConditionBlueprintType.emptiness:
            case conditionBlueprints_1.ConditionBlueprintType.futureDate:
            case conditionBlueprints_1.ConditionBlueprintType.lastIncidentDate:
            case conditionBlueprints_1.ConditionBlueprintType.matches:
            case conditionBlueprints_1.ConditionBlueprintType.matchesRegex:
            case conditionBlueprints_1.ConditionBlueprintType.numberComparison:
            case conditionBlueprints_1.ConditionBlueprintType.percentOf:
            case conditionBlueprints_1.ConditionBlueprintType.reflexive:
                return [conditionValues.targetNodeId];
            case conditionBlueprints_1.ConditionBlueprintType.dateComparison:
                return [conditionValues.startDateNodeId, conditionValues.endDateNodeId];
            case conditionBlueprints_1.ConditionBlueprintType.mathOperator:
                return conditionValues.nodeIds;
            case conditionBlueprints_1.ConditionBlueprintType.engineCondition:
            case conditionBlueprints_1.ConditionBlueprintType.instancesCount:
            case conditionBlueprints_1.ConditionBlueprintType.jointProductAgeRange:
                return [];
        }
    }
    toBooleanOperator(operator) {
        switch (operator) {
            case 'or':
                return operators_1.BooleanOperators.or;
            case 'not':
                return operators_1.BooleanOperators.not;
            default:
                return operators_1.BooleanOperators.and;
        }
    }
    toCollectionOperator(operator) {
        if (!operator)
            return null;
        switch (operator) {
            case conditionBlueprints_1.BlueprintCollectionOperators.every:
                return operators_1.CollectionOperators.every;
            case conditionBlueprints_1.BlueprintCollectionOperators.some:
                return operators_1.CollectionOperators.some;
            case conditionBlueprints_1.BlueprintCollectionOperators.none:
                return operators_1.CollectionOperators.none;
            case conditionBlueprints_1.BlueprintCollectionOperators.thisItem:
                return null;
        }
    }
    getAgeRoundingType(roundingType, unit = dateTime_1.DateUnits.year) {
        let ageRoundingType;
        switch (roundingType) {
            case 'closestBirthday':
                ageRoundingType = dateTime_1.AgeRoundingTypes.closestBirthday;
                break;
            case 'lastBirthday':
                ageRoundingType = dateTime_1.AgeRoundingTypes.lastBirthday;
                break;
            case 'nextBirthday':
                ageRoundingType = dateTime_1.AgeRoundingTypes.nextBirthday;
                break;
            case 'none':
                ageRoundingType = dateTime_1.AgeRoundingTypes.none;
                break;
            default:
                if (unit === dateTime_1.DateUnits.month || unit === dateTime_1.DateUnits.day) {
                    ageRoundingType = dateTime_1.AgeRoundingTypes.none;
                }
                else {
                    ageRoundingType = dateTime_1.AgeRoundingTypes.closestBirthday;
                }
        }
        return ageRoundingType;
    }
    generateAgeRangeCondition(conditionValue) {
        const { targetBirthdateNodeId, value: { minAge, maxAge, unit = dateTime_1.DateUnits.year }, roundingType, } = conditionValue;
        const ageRoundingType = this.getAgeRoundingType(roundingType, unit);
        return {
            conditions: [
                (0, date_1.minimumAgeCondition)(targetBirthdateNodeId, minAge, unit, ageRoundingType),
                (0, date_1.maximumAgeCondition)(targetBirthdateNodeId, maxAge, unit, ageRoundingType),
            ],
            operator: operators_1.BooleanOperators.and,
        };
    }
    generateCharacterCountInBetweenCondition(conditionValue) {
        const { targetNodeId, value: { minLength, maxLength }, } = conditionValue;
        const minLimit = minLength && minLength >= 0 ? minLength - 1 : -1;
        const maxLimit = maxLength ? maxLength + 1 : Number.MAX_SAFE_INTEGER;
        return {
            nodeId: targetNodeId,
            operator: operators_1.ComparisonOperators.characterCountBetween,
            value: [minLimit, maxLimit],
        };
    }
    generateMathOperatorCondition(conditionValue) {
        const { value, nodeIds, operator, mathOperator, nodeIdOfValue } = conditionValue;
        if (typeof value === 'undefined' && !nodeIdOfValue) {
            throw new Error(`need either a value or a nodeIdOfValue to generate a math operator condition`);
        }
        const comparisonOperator = this.numberComparisonConditionOperatorToComparisonOperator(operator);
        let queryOperator;
        switch (mathOperator) {
            case conditionBlueprints_1.MathConditionOperators.sum: {
                queryOperator = queryOperators_2.QueryOperators.sum;
                break;
            }
            case conditionBlueprints_1.MathConditionOperators.multiply: {
                queryOperator = queryOperators_2.QueryOperators.multiply;
                break;
            }
            case conditionBlueprints_1.MathConditionOperators.subtract: {
                queryOperator = queryOperators_2.QueryOperators.subtract;
                break;
            }
        }
        const condition = {
            query: {
                select: nodeIds,
                // Note: `selectRepeatable` could be conditionally set here if we wanted aggregation features. (See `generateNumberComparisonCondition`)
                operator: queryOperator,
            },
            operator: comparisonOperator,
        };
        if (nodeIdOfValue && !this.nodeIdParents.has(nodeIdOfValue)) {
            throw new Error(`nodeIdOfValue ${nodeIdOfValue} is not in the nodeIdToAnswerPathMap`);
        }
        if (nodeIdOfValue) {
            condition.controlValue = nodeIdOfValue;
        }
        else if (typeof value !== 'undefined') {
            condition.value = value;
        }
        return condition;
    }
    generateInstancesCountCondition(conditionValue) {
        const { limitOf, minimumOf, nodeIdsToAdd, nodeIdsToSubtract } = conditionValue;
        const selectRepeatable = [];
        const select = [];
        [...nodeIdsToAdd, ...nodeIdsToSubtract].forEach(({ nodeId, repeatableCollectionNodeId }) => {
            if (!repeatableCollectionNodeId) {
                select.push(nodeId);
            }
            else {
                selectRepeatable.push({ fromCollection: repeatableCollectionNodeId, select: nodeId });
            }
        });
        const query = {
            select,
            selectRepeatable,
            operator: queryOperators_2.QueryOperators.formula,
            operatorParams: {
                formula: `getRepeatedCount(VALUES, nodeIdsToAdd, nodeIdsToSubtract)`,
                formulaParams: {
                    nodeIdsToAdd,
                    nodeIdsToSubtract,
                },
            },
        };
        const conditions = [];
        if (limitOf) {
            conditions.push({
                operator: operators_1.ComparisonOperators.lessThanOrEqual,
                value: limitOf,
                query,
            });
        }
        if (minimumOf) {
            conditions.push({
                operator: operators_1.ComparisonOperators.greaterThanOrEqual,
                value: minimumOf,
                query,
            });
        }
        return {
            conditions,
            operator: operators_1.BooleanOperators.and,
        };
    }
    generateJointProductAgeRangeCondition(conditionValue) {
        const { jointProductNodeIds, value, roundingType, repeatableInsuredCollectionId, insuredBirthdayNodeId } = conditionValue;
        const { unit, maxAge, minAge } = value;
        const transformedRoundingType = this.getAgeRoundingType(roundingType, unit);
        const fetchExistingJointProductSurrogateIdQuery = (0, fetchJointProductSurrogateId_1.fetchExistingJointProductSurrogateId)(repeatableInsuredCollectionId, jointProductNodeIds);
        const fetchOldestJointProductBirthday = {
            select: [],
            operator: queryOperators_2.QueryOperators.formula,
            operatorParams: {
                formula: `keepOldestDate(VALUES)`,
                formulaParams: {},
            },
            selectRepeatable: [
                {
                    fromCollection: repeatableInsuredCollectionId,
                    select: insuredBirthdayNodeId,
                    selectWithConditions: {
                        nodeId: insuredBirthdayNodeId,
                        selectIf: {
                            operator: operators_1.BooleanOperators.or,
                            conditions: jointProductNodeIds.map((jointProductNodeId) => ({
                                nodeId: jointProductNodeId.surrogateId,
                                operator: operators_1.ComparisonOperators.equal,
                                query: fetchExistingJointProductSurrogateIdQuery,
                            })),
                        },
                        defaultValue: undefined,
                    },
                },
            ],
        };
        const fetchNewestJointProductBirthday = {
            select: [],
            operator: queryOperators_2.QueryOperators.formula,
            operatorParams: {
                formula: `keepNewestDate(VALUES)`,
                formulaParams: {},
            },
            selectRepeatable: [
                {
                    fromCollection: repeatableInsuredCollectionId,
                    select: insuredBirthdayNodeId,
                    selectWithConditions: {
                        nodeId: insuredBirthdayNodeId,
                        selectIf: {
                            operator: operators_1.BooleanOperators.or,
                            conditions: jointProductNodeIds.map((jointProductNodeId) => ({
                                nodeId: jointProductNodeId.surrogateId,
                                operator: operators_1.ComparisonOperators.equal,
                                query: fetchExistingJointProductSurrogateIdQuery,
                            })),
                        },
                        defaultValue: undefined,
                    },
                },
            ],
        };
        return {
            conditions: [
                {
                    query: fetchOldestJointProductBirthday,
                    operator: operators_1.ComparisonOperators.greaterThanOrEqual,
                    controlValueQuery: {
                        select: [dateTime_1.DateTimeReference.currentDateTime],
                        operator: queryOperators_2.QueryOperators.shiftDateBackwards,
                        operatorParams: {
                            dateUnit: unit,
                            shiftDateParams: {
                                shiftMagnitude: dateTime_1.ShiftMagnitudes.earliestDateRespectingShiftValue,
                                dateFormat: 'YYYY-MM-DD',
                                shiftValue: maxAge,
                                timeRoundingType: transformedRoundingType,
                            },
                        },
                    },
                },
                ...(minAge
                    ? [
                        {
                            query: fetchNewestJointProductBirthday,
                            operator: operators_1.ComparisonOperators.lessThanOrEqual,
                            controlValueQuery: {
                                select: [dateTime_1.DateTimeReference.currentDateTime],
                                operator: queryOperators_2.QueryOperators.shiftDateBackwards,
                                operatorParams: {
                                    dateUnit: unit,
                                    shiftDateParams: {
                                        shiftMagnitude: dateTime_1.ShiftMagnitudes.earliestDateRespectingShiftValue,
                                        dateFormat: 'YYYY-MM-DD',
                                        shiftValue: minAge,
                                        timeRoundingType: transformedRoundingType,
                                    },
                                },
                            },
                        },
                    ]
                    : []),
            ],
            operator: operators_1.BooleanOperators.and,
        };
    }
    generateBMIRangeCondition(conditionValue) {
        const { value, heightUnit, weightUnit, heightNodeId, weightNodeId } = conditionValue;
        const formattedWeightNodeId = (0, queryOperators_1.formatFormulaVariableName)(weightNodeId);
        const weightInKilograms = weightUnit === 'lb' ? `(${formattedWeightNodeId} * 0.453592)` : formattedWeightNodeId;
        const formattedHeightNodeId = (0, queryOperators_1.formatFormulaVariableName)(heightNodeId);
        const heightInMeters = heightUnit === 'cm' ? `(${formattedHeightNodeId} * 0.01)` : `(${formattedHeightNodeId} * 0.0254)`;
        return {
            query: {
                select: [heightNodeId, weightNodeId],
                operator: queryOperators_2.QueryOperators.formula,
                operatorParams: {
                    formula: `${weightInKilograms} / ${heightInMeters}^2`,
                },
            },
            value: [value.minBMI, value.maxBMI],
            operator: operators_1.ComparisonOperators.inRange,
        };
    }
    generateNumberComparisonCondition(conditionValue) {
        const { targetNodeId, value, operator, nodeIdOfValue, collectionOperators } = conditionValue;
        if (typeof value === 'undefined' && !nodeIdOfValue) {
            throw new Error(`need either a value or a nodeIdOfValue to generate a math operator condition`);
        }
        if (nodeIdOfValue && !this.nodeIdParents.has(nodeIdOfValue)) {
            throw new Error(`nodeIdOfValue ${nodeIdOfValue} is not in the nodeIdToAnswerPathMap`);
        }
        const collectionNodeIds = this.nodeIdParents.get(targetNodeId) || [];
        const collectionNodeIdsWithoutThisItem = collectionNodeIds.filter((nodeId) => collectionOperators?.[nodeId] && collectionOperators[nodeId] !== conditionBlueprints_1.BlueprintCollectionOperators.thisItem);
        const latestCollectionNodeId = lodash_1.default.last(collectionNodeIdsWithoutThisItem);
        const collectionOperator = latestCollectionNodeId ? collectionOperators?.[latestCollectionNodeId] : null;
        const comparisonOperator = this.numberComparisonConditionOperatorToComparisonOperator(operator);
        // `shouldAggregate` depends on the last `collectionOperator` provided, but this is an imperfect measure. We could have a toggle to determine whether to aggregate the numeric value or not.
        const shouldAggregate = !!collectionOperator && collectionOperator !== conditionBlueprints_1.BlueprintCollectionOperators.thisItem;
        if (shouldAggregate && latestCollectionNodeId) {
            const collectionCondition = {
                query: {
                    select: [],
                    selectRepeatable: [
                        {
                            fromCollection: latestCollectionNodeId,
                            select: targetNodeId,
                        },
                    ],
                    operator: queryOperators_2.QueryOperators.sum,
                },
                operator: comparisonOperator,
            };
            if (nodeIdOfValue) {
                collectionCondition.controlValue = nodeIdOfValue;
            }
            else if (typeof value !== 'undefined') {
                collectionCondition.value = value;
            }
            return {
                conditions: [collectionCondition],
            };
        }
        else {
            const condition = {
                operator: comparisonOperator,
                nodeId: targetNodeId,
            };
            if (nodeIdOfValue) {
                condition.controlValue = nodeIdOfValue;
            }
            else if (typeof value !== 'undefined') {
                condition.value = value;
            }
            return condition;
        }
    }
    generateEngineCondition(conditionValue) {
        return conditionValue.condition;
    }
    generateMatchesRegexCondition(conditionValue) {
        return {
            value: conditionValue.regex,
            operator: operators_1.ComparisonOperators.matchesRegex,
            nodeId: conditionValue.targetNodeId,
        };
    }
    generateCountEqualCondition(conditionValue) {
        const { value, controlValue, targetNodeId, operator } = conditionValue;
        const numberComparisonOperator = this.numberComparisonConditionOperatorToComparisonOperator(operator);
        const collectionNodeIds = this.nodeIdParents.get(targetNodeId);
        const latestCollectionNodeId = lodash_1.default.last(collectionNodeIds);
        if (!latestCollectionNodeId) {
            throw new Error(`on countEqual condition: no collection answer path was found for nodeId ${targetNodeId}`);
        }
        return {
            conditions: [
                {
                    query: {
                        select: [],
                        selectRepeatable: [
                            {
                                fromCollection: latestCollectionNodeId,
                                select: targetNodeId,
                            },
                        ],
                        operator: queryOperators_2.QueryOperators.countEqual,
                        operatorParams: { controlValue },
                    },
                    operator: numberComparisonOperator,
                    value,
                },
            ],
        };
    }
    generateFutureDateCondition(conditionValue) {
        const { targetNodeId, value, unit, operator } = conditionValue;
        const comparisonOperator = this.numberComparisonConditionOperatorToComparisonOperator(operator);
        return (0, date_1.futureDateCondition)(targetNodeId, value, unit, comparisonOperator);
    }
    generateLastIncidentDateCondition(conditionValue) {
        const { targetNodeId, value, unit, operator } = conditionValue;
        const comparisonOperator = this.numberComparisonConditionOperatorToComparisonOperator(operator);
        return (0, date_1.lastIncidentDateCondition)(targetNodeId, value, unit, comparisonOperator);
    }
    numberComparisonConditionOperatorToComparisonOperator(operator) {
        let comparisonOperator;
        switch (operator) {
            case conditionBlueprints_1.NumberComparisonConditionOperators.equal: {
                comparisonOperator = operators_1.ComparisonOperators.equal;
                break;
            }
            case conditionBlueprints_1.NumberComparisonConditionOperators.lessThan: {
                comparisonOperator = operators_1.ComparisonOperators.lessThan;
                break;
            }
            case conditionBlueprints_1.NumberComparisonConditionOperators.lessThanOrEqual: {
                comparisonOperator = operators_1.ComparisonOperators.lessThanOrEqual;
                break;
            }
            case conditionBlueprints_1.NumberComparisonConditionOperators.greaterThan: {
                comparisonOperator = operators_1.ComparisonOperators.greaterThan;
                break;
            }
            case conditionBlueprints_1.NumberComparisonConditionOperators.greaterThanOrEqual: {
                comparisonOperator = operators_1.ComparisonOperators.greaterThanOrEqual;
                break;
            }
            case conditionBlueprints_1.NumberComparisonConditionOperators.multipleOf: {
                comparisonOperator = operators_1.ComparisonOperators.multipleOf;
                break;
            }
        }
        return comparisonOperator;
    }
}
exports.ConditionsBuilder = ConditionsBuilder;
